// ===========================================
// Events React Query Hooks (Admin)
// ===========================================
// Custom hooks for admin event management and public event queries

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  eventApi,
  eventPublicApi,
  EventFilters,
  EventSubListFilters,
  UpdateEventData,
  RSVPResponse,
  TicketStatus,
  MarkAttendanceItem,
  AddPixabayPhotoData,
  PublicEventRegistrationData,
  PublicTicketCheckoutData,
  UpdateEventVenueData,
} from '../api/events.api';
import { locationsApi } from '../api/admin.api';
import { calendarKeys } from './useHybridBooking';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  list: (filters?: EventFilters) => [...eventKeys.lists(), filters] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  detail: (id: string) => [...eventKeys.details(), id] as const,
  stats: () => [...eventKeys.all, 'stats'] as const,
  rsvps: (eventId: string) => [...eventKeys.all, 'rsvps', eventId] as const,
  rsvpStats: (eventId: string) => [...eventKeys.all, 'rsvpStats', eventId] as const,
  registrations: (eventId: string) => [...eventKeys.all, 'registrations', eventId] as const,
  photos: (eventId: string) => [...eventKeys.all, 'photos', eventId] as const,
  tickets: (eventId: string) => [...eventKeys.all, 'tickets', eventId] as const,
  attendance: (eventId: string) => [...eventKeys.all, 'attendance', eventId] as const,
  // Public event keys (scoped by school slug)
  publicLists: (slug: string) => [...eventKeys.all, 'public', slug, 'list'] as const,
  publicDetail: (slug: string, eventSlug: string) =>
    [...eventKeys.all, 'public', slug, 'detail', eventSlug] as const,
};

// ===========================================
// SHARED ERROR TYPE
// ===========================================

type ApiError = Error & { response?: { data?: { message?: string } } };

// ===========================================
// ADMIN QUERY HOOKS
// ===========================================

/**
 * Get paginated list of events with optional filters
 */
export function useEvents(filters?: EventFilters) {
  return useQuery({
    queryKey: eventKeys.list(filters),
    queryFn: () => eventApi.list(filters),
  });
}

/**
 * Get single event by ID (full detail)
 */
export function useEvent(id: string) {
  return useQuery({
    queryKey: eventKeys.detail(id),
    queryFn: () => eventApi.get(id),
    enabled: !!id,
  });
}

/**
 * Get event counts by status
 */
export function useEventStats() {
  return useQuery({
    queryKey: eventKeys.stats(),
    queryFn: () => eventApi.getStats(),
  });
}

/**
 * List RSVPs for an event
 */
export function useEventRsvps(eventId: string, filters?: EventSubListFilters) {
  return useQuery({
    queryKey: [...eventKeys.rsvps(eventId), filters],
    queryFn: () => eventApi.listRsvps(eventId, filters),
    enabled: !!eventId,
  });
}

/**
 * Get RSVP statistics for an event
 */
export function useEventRsvpStats(eventId: string) {
  return useQuery({
    queryKey: eventKeys.rsvpStats(eventId),
    queryFn: () => eventApi.getRsvpStats(eventId),
    enabled: !!eventId,
  });
}

/**
 * List public registrations for an event
 */
export function useEventRegistrations(eventId: string, filters?: EventSubListFilters) {
  return useQuery({
    queryKey: [...eventKeys.registrations(eventId), filters],
    queryFn: () => eventApi.listRegistrations(eventId, filters),
    enabled: !!eventId,
  });
}

/**
 * List photos for an event
 */
export function useEventPhotos(eventId: string) {
  return useQuery({
    queryKey: eventKeys.photos(eventId),
    queryFn: () => eventApi.listPhotos(eventId),
    enabled: !!eventId,
  });
}

/**
 * List tickets for an event
 */
export function useEventTickets(eventId: string, status?: TicketStatus) {
  return useQuery({
    queryKey: eventKeys.tickets(eventId),
    queryFn: () => eventApi.listTickets(eventId, status),
    enabled: !!eventId,
  });
}

/**
 * List attendance records for an event
 */
export function useEventAttendance(eventId: string) {
  return useQuery({
    queryKey: eventKeys.attendance(eventId),
    queryFn: () => eventApi.listAttendance(eventId),
    enabled: !!eventId,
  });
}

/**
 * Search Pixabay on demand — returns a mutation so callers control when it fires.
 */
export function usePixabaySearch() {
  return useMutation({
    mutationFn: ({ q, page }: { q: string; page?: number }) =>
      eventApi.searchPixabay(q, page),
  });
}

// ===========================================
// ADMIN MUTATION HOOKS
// ===========================================

/**
 * Create a new event (starts as DRAFT)
 */
export function useCreateEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: eventApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() });
      enqueueSnackbar('Event created', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to create event';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update event fields
 */
export function useUpdateEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEventData }) =>
      eventApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      enqueueSnackbar('Event updated', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to update event';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Publish a draft event
 */
export function usePublishEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => eventApi.publish(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() });
      enqueueSnackbar('Event published', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to publish event';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Cancel a published event
 */
export function useCancelEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      eventApi.cancel(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() });
      enqueueSnackbar('Event cancelled', { variant: 'info' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to cancel event';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Mark a published event as completed
 */
export function useCompleteEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => eventApi.complete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: eventKeys.stats() });
      enqueueSnackbar('Event marked as completed', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to complete event';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete an event (DRAFT or CANCELLED only)
 */
export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => eventApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Event deleted', { variant: 'info' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to delete event';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update a reusable venue (used by the "Adjust pin" modal to save a manual
 * lat/lng or trigger a re-geocode). Pass the eventId so the event's detail
 * query is invalidated and its venue map refreshes with the new coords.
 */
export function useUpdateVenue() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      venueId,
      data,
    }: {
      venueId: string;
      data: UpdateEventVenueData;
      eventId?: string;
    }) => eventApi.updateVenue(venueId, data),
    onSuccess: (_, { eventId }) => {
      if (eventId) queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Venue location updated', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to update venue location';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update a school Location's pin (used by the "Adjust pin" modal for events held
 * at a school Location rather than an external venue). Pass the eventId so the
 * event's detail query is invalidated and its map refreshes with the new coords.
 */
export function useUpdateLocationCoords() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      locationId,
      data,
    }: {
      locationId: string;
      data: { latitude?: number | null; longitude?: number | null; regeocode?: boolean };
      eventId?: string;
    }) => locationsApi.update(locationId, data),
    onSuccess: (_, { eventId }) => {
      if (eventId) queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) });
      queryClient.invalidateQueries({ queryKey: eventKeys.lists() });
      enqueueSnackbar('Location updated', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to update location';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Update the response on a specific RSVP
 */
export function useUpdateRsvpStatus() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      eventId,
      rsvpId,
      response,
    }: {
      eventId: string;
      rsvpId: string;
      response: RSVPResponse;
    }) => eventApi.updateRsvpStatus(eventId, rsvpId, response),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.rsvps(eventId) });
      queryClient.invalidateQueries({ queryKey: eventKeys.rsvpStats(eventId) });
      enqueueSnackbar('RSVP updated', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to update RSVP';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Convert a public registration into a family account
 */
export function useConvertRegistration() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ eventId, registrationId }: { eventId: string; registrationId: string }) =>
      eventApi.convertRegistration(eventId, registrationId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.registrations(eventId) });
      enqueueSnackbar('Registration converted to family account', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to convert registration';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Bulk-mark attendance for an event
 */
export function useMarkAttendance() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      eventId,
      attendances,
    }: {
      eventId: string;
      attendances: MarkAttendanceItem[];
    }) => eventApi.markAttendance(eventId, attendances),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.attendance(eventId) });
      enqueueSnackbar('Attendance recorded', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to record attendance';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Check in a ticket holder by QR token
 */
export function useCheckIn() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ eventId, qrToken }: { eventId: string; qrToken: string }) =>
      eventApi.checkIn(eventId, qrToken),
    onSuccess: (result, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.tickets(eventId) });
      queryClient.invalidateQueries({ queryKey: eventKeys.attendance(eventId) });
      if (result.alreadyCheckedIn) {
        enqueueSnackbar(`${result.holderName} already checked in`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`${result.holderName} checked in`, { variant: 'success' });
      }
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Check-in failed';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Add a Pixabay photo to an event gallery
 */
export function useAddPixabayPhoto() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: AddPixabayPhotoData }) =>
      eventApi.addPixabayPhoto(eventId, data),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.photos(eventId) });
      enqueueSnackbar('Photo added', { variant: 'success' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to add photo';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Delete a photo from an event gallery
 */
export function useDeletePhoto() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ eventId, photoId }: { eventId: string; photoId: string }) =>
      eventApi.deletePhoto(eventId, photoId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: eventKeys.photos(eventId) });
      enqueueSnackbar('Photo deleted', { variant: 'info' });
    },
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to delete photo';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// PUBLIC QUERY HOOKS
// ===========================================

/**
 * List PUBLISHED + isPublic upcoming events for a school (no auth)
 */
export function usePublicEvents(
  slug: string,
  params?: { page?: number; limit?: number }
) {
  return useQuery({
    queryKey: eventKeys.publicLists(slug),
    queryFn: () => eventPublicApi.list(slug, params),
    enabled: !!slug,
  });
}

/**
 * Get a single PUBLISHED + isPublic event by slug (no auth)
 */
export function usePublicEvent(slug: string, eventSlug: string) {
  return useQuery({
    queryKey: eventKeys.publicDetail(slug, eventSlug),
    queryFn: () => eventPublicApi.getBySlug(slug, eventSlug),
    enabled: !!slug && !!eventSlug,
  });
}

/**
 * Anonymous public registration (lead-capture) for a public event
 */
export function useRegisterForEvent() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      slug,
      eventSlug,
      data,
    }: {
      slug: string;
      eventSlug: string;
      data: PublicEventRegistrationData;
    }) => eventPublicApi.register(slug, eventSlug, data),
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to submit registration. Please try again.';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Guest Stripe Checkout session for a paid public event
 */
export function usePublicTicketCheckout() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      slug,
      eventSlug,
      data,
    }: {
      slug: string;
      eventSlug: string;
      data: PublicTicketCheckoutData;
    }) => eventPublicApi.checkout(slug, eventSlug, data),
    onError: (error: ApiError) => {
      const message = error.response?.data?.message || 'Failed to initiate checkout. Please try again.';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
